
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Button } from "@/components/ui/button"
import { ChevronLeft } from "lucide-react"
import { useNavigate } from "react-router-dom"
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'

const mockSleepWindowsData = [
  { hour_label: '6:00 PM', sleep_start_count: 1, sleep_end_count: 0 },
  { hour_label: '7:00 PM', sleep_start_count: 3, sleep_end_count: 0 },
  { hour_label: '8:00 PM', sleep_start_count: 5, sleep_end_count: 0 },
  { hour_label: '9:00 PM', sleep_start_count: 2, sleep_end_count: 0 },
  { hour_label: '5:00 AM', sleep_start_count: 0, sleep_end_count: 1 },
  { hour_label: '6:00 AM', sleep_start_count: 0, sleep_end_count: 4 },
  { hour_label: '7:00 AM', sleep_start_count: 0, sleep_end_count: 5 },
  { hour_label: '8:00 AM', sleep_start_count: 0, sleep_end_count: 1 },
]

const mockDurationData = [
  { duration: '4-6 hrs', count: 1 },
  { duration: '6-8 hrs', count: 3 },
  { duration: '8-10 hrs', count: 5 },
  { duration: '10-12 hrs', count: 2 },
  { duration: '12+ hrs', count: 1 },
]

export default function SleepTutorial() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background p-6 space-y-8">
      <div className="space-y-4">
        <Button 
          variant="ghost" 
          onClick={() => navigate(-1)} 
          className="flex items-center gap-2 mb-4"
        >
          <ChevronLeft className="h-4 w-4" />
          Back
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Understanding Sleep Pattern Analysis</h1>
        <p className="text-muted-foreground">A guide to interpreting sleep data visualizations</p>
      </div>

      <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/50 dark:to-blue-950/50 border-none">
        <CardHeader>
          <CardTitle className="text-xl font-semibold bg-clip-text text-transparent bg-gradient-to-r from-purple-600 to-blue-600 dark:from-purple-400 dark:to-blue-400">
            Understanding Your Sleep Charts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[480px] pr-4">
            <div className="prose dark:prose-invert max-w-none space-y-8">
              {/* Sleep Windows Story */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Sleep Windows: The Story of Nibbles</h3>
                <p className="text-muted-foreground leading-relaxed">
                  In the heart of the Whispering Woods, there lived a family of squirrels, and the youngest, Nibbles, 
                  was always tired. His parents thought he just needed to sleep more, so they tucked him in earlier, 
                  let him nap longer, and even tried bedtime acorn snacks. But Nibbles still woke up grumpy, his tiny 
                  tail dragging behind him.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  That's when Grandma Nutmeg, the wisest squirrel in the tree, decided to track Nibbles' sleep. She 
                  gathered his nap times like little acorns, counted how long he slept each day, and sorted them into 
                  cozy piles—short, medium, and long naps.
                </p>
                <div className="h-[300px] mt-4 bg-white dark:bg-gray-900 rounded-lg p-4 shadow-inner">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={mockSleepWindowsData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="hour_label" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="sleep_start_count" name="Sleep Starts" fill="#8884d8" />
                      <Bar dataKey="sleep_end_count" name="Wake-ups" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="font-medium text-purple-700 dark:text-purple-300">
                  The lesson? It's not just about how many naps you take, but when your body naturally wants to rest.
                </p>
              </div>

              {/* Sleep Duration Story */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Sleep Duration Distribution: The Tale of Luna the Bear</h3>
                <p className="text-muted-foreground leading-relaxed">
                  Luna was a little bear who loved to play all day in the Sleepy Valley. Some nights she slept for just 
                  a little while, and other nights she slept so long her mom had to wake her up for breakfast! Her forest 
                  friends were worried - was this normal?
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Dr. Owl, the forest's sleep expert, helped Luna's family track her sleep. They made a special chart 
                  showing how long Luna slept each night. Soon, they discovered that Luna usually slept between 8-10 
                  hours - just right for a growing bear!
                </p>
                <div className="h-[300px] mt-4 bg-white dark:bg-gray-900 rounded-lg p-4 shadow-inner">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={mockDurationData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="duration" />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="count" name="Nights" fill="#82ca9d" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
                <p className="font-medium text-purple-700 dark:text-purple-300">
                  The lesson? Every child has their own sleep pattern, but looking at the bigger picture helps us understand what's normal for them.
                </p>
              </div>

              {/* Sleep Latency Story */}
              <div className="space-y-4">
                <h3 className="text-lg font-medium">Sleep Latency: The Tale of Hazel the Hedgehog</h3>
                <p className="text-muted-foreground leading-relaxed">
                  In the magical town of Snuggle Hollow, there was a little hedgehog named Hazel who loved bedtime stories. 
                  But while her friends drifted off to dreamland quickly, Hazel would lie awake, counting imaginary bugs 
                  instead of sheep. Her parents thought maybe she wasn't tired enough, so they played more games. Then they 
                  thought she was too tired, so they moved bedtime earlier. Nothing worked!
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  That's when Dr. Doze, the sleepy sloth scientist, decided to investigate. He tracked how long it took 
                  Hazel to fall asleep after bedtime—her sleep latency—and compared it to other young critters. When he 
                  laid out the data, it was clear: Hazel took much longer to fall asleep, especially on days when she had 
                  screen time before bed.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  They made small changes—less screen time, a cozy wind-down routine—and soon, Hazel was snoozing faster 
                  than you could say "bedtime!"
                </p>
                <p className="font-medium text-purple-700 dark:text-purple-300">
                  The lesson? Sometimes it's not about when you go to bed, but how easily you can drift off.
                </p>
              </div>

              <div className="space-y-4">
                <h3 className="text-lg font-medium text-blue-700 dark:text-blue-300">How to Read the Charts</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  <div>
                    <h4 className="font-medium mb-2">Sleep Windows Chart</h4>
                    <p className="text-muted-foreground">
                      Shows when sleep typically starts (blue bars) and ends (green bars). The higher the bars, 
                      the more common that time is for sleep starts or wake-ups.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Sleep Duration Distribution</h4>
                    <p className="text-muted-foreground">
                      Displays how long each sleep session typically lasts, helping identify if sleep is fragmented 
                      or occurring in healthy, consolidated stretches.
                    </p>
                  </div>
                  <div>
                    <h4 className="font-medium mb-2">Sleep Latency Chart</h4>
                    <p className="text-muted-foreground">
                      Shows how long it typically takes to fall asleep. The bars represent the frequency of different 
                      time ranges, helping identify potential sleep onset issues.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
